import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useCurrentUser } from "@av/apps/common-apps/platform/services/authentication/user/current-user";
import { bffAxios } from "@av/config/api-config/axios-config";
import { platformQueryKeys } from "../platform-query-key-factory";
import {
  type ApiError,
  type AtsAdminSettingsUser,
  type CreatePlatformUserModel,
  type EmailStatus,
  type HrAdminSettingsUser,
  type HrUserBulkUpdateModel,
  type HrUserUpdateModel,
  type PlatformUser,
  type ProductCode,
  securityGroupsQueryKeyMap,
  usersQueryKeyMap,
} from "./type";

import type { AppId } from "../apps/types";
import type { TFunction } from "i18next";

type AdminSettingsAlert = {
  product: AppId;
  message: string;
};

export type AdminSettingsPermissionsByProduct = Record<ProductCode, string[]>;

export function resolveAdminSettingsRolesAndPermissionsTitle(
  t: TFunction,
  selectedSection: string
): string {
  const translationMap: Partial<Record<string, string>> = {
    recruitment: t("platform.adminSettings.rolesAndPermissionTitle_ats"),
    hr: t("platform.adminSettings.rolesAndPermissionTitle_hr"),
    payroll: t("platform.adminSettings.rolesAndPermissionTitle_payroll"),
  };

  return translationMap[selectedSection] ?? t("platform.adminSettings.rolesAndPermissions");
}

export function resolveEmailStatusLabel(
  t: TFunction,
  emailStatus?: EmailStatus
): string | undefined {
  if (!emailStatus) {
    return undefined;
  }

  const translationMap: Record<EmailStatus, string> = {
    SENT: t("platform.adminSettings.users_emailStatus_sent"),
    FAILED: t("platform.adminSettings.users_emailStatus_failed"),
  };

  return translationMap[emailStatus];
}

export function getAdminSettingsAlerts(
  t: TFunction,
  companyAvailableApps: AppId[]
): AdminSettingsAlert[] {
  const alerts: AdminSettingsAlert[] = [];

  if (!companyAvailableApps.includes("ATS")) {
    alerts.push({
      product: "ATS",
      message: t("platform.adminSettings.toolTip_upsellMessage_ats"),
    });
  }

  return alerts;
}

export function useCompanyAvailableApps(enabled = true) {
  const { data: currentUser } = useCurrentUser();
  const companyId = currentUser?.company.id;

  return useQuery({
    queryKey: platformQueryKeys.companyApps(companyId),
    enabled: Boolean(companyId) && enabled,
    queryFn: async (): Promise<AppId[]> => {
      const { data } = await bffAxios.get<AppId[]>(`/products/${companyId}/available-apps`);

      return data;
    },
  });
}

const atsAssignmentPollingDeadlineMs = 2 * 60 * 1_000;

export const useFetchAtsAdminSettingsUsers = () => {
  const queryClient = useQueryClient();
  const readinessPollingStartedAt = useRef(new Map<string, number>());

  return useQuery<AtsAdminSettingsUser[]>({
    queryKey: platformQueryKeys.atsUsers(),
    queryFn: () => fetchAtsAdminSettingsUsers(queryClient),
    staleTime: Infinity,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      const hasPendingProvisioningUsers = query.state.data?.some(
        (user) => user.atsUserProvisioningPending === true
      );
      const pendingUserIds = (query.state.data ?? [])
        .filter((user) => user.atsUserProvisioningPending === true)
        .map((user) => user.userId);

      pendingUserIds.forEach((userId) => {
        readinessPollingStartedAt.current.set(
          userId,
          readinessPollingStartedAt.current.get(userId) ?? Date.now()
        );
      });
      readinessPollingStartedAt.current.forEach((_startedAt, userId) => {
        if (!pendingUserIds.includes(userId)) {
          readinessPollingStartedAt.current.delete(userId);
        }
      });

      if (!hasPendingProvisioningUsers) {
        return false;
      }

      const now = Date.now();

      return pendingUserIds.some(
        (userId) =>
          now - (readinessPollingStartedAt.current.get(userId) ?? now) <
          atsAssignmentPollingDeadlineMs
      )
        ? 1_000
        : false;
    },
  });
};

async function fetchAtsAdminSettingsUsers(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<AtsAdminSettingsUser[]> {
  const { data } = await bffAxios.get<{ items: AtsAdminSettingsUser[] }>("/users/ats");
  const previousUsers =
    queryClient.getQueryData<AtsAdminSettingsUser[]>(platformQueryKeys.atsUsers()) ?? [];
  const serverUserIds = new Set(data.items.map((user) => user.userId));
  const pendingUsersNotYetReturned = previousUsers.filter(
    (user) => user.atsUserProvisioningPending === true && !serverUserIds.has(user.userId)
  );

  return [
    ...data.items.map((user) => {
      const previousUser = previousUsers.find((candidate) => candidate.userId === user.userId);

      return previousUser?.atsUserProvisioningPending && user.atsUserReady !== true
        ? { ...user, atsUserProvisioningPending: true }
        : user;
    }),
    ...pendingUsersNotYetReturned,
  ];
}

export const useFetchHrAdminSettingsUsers = () => {
  return useQuery<HrAdminSettingsUser[]>({
    queryKey: platformQueryKeys.hrUsers(),
    queryFn: async (): Promise<HrAdminSettingsUser[]> => {
      const { data } = await bffAxios.get<{ items: HrAdminSettingsUser[] }>("/users/hr");

      return data.items;
    },
    staleTime: Infinity,
    refetchOnMount: "always",
  });
};

export const useFetchAdminSettingsPermissions = (enabled = true) => {
  return useQuery<AdminSettingsPermissionsByProduct>({
    queryKey: platformQueryKeys.permissions(),
    enabled,
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await bffAxios.get<AdminSettingsPermissionsByProduct>(
        "/admin-settings/permissions"
      );

      return data;
    },
  });
};

export const useFetchHrPermissions = (enabled = true) => {
  const query = useFetchAdminSettingsPermissions(enabled);

  return {
    ...query,
    data: query.data?.hr ?? [],
  };
};

export const usePermission = (permission: string, enabled = true) => {
  const query = useFetchHrPermissions(enabled);

  return query.data.includes(permission);
};

export const useFetchPermissions = (productCode: ProductCode | undefined, enabled = true) => {
  const query = useFetchAdminSettingsPermissions(Boolean(productCode) && enabled);

  return {
    isGlobalPermissionsLoading: query.isLoading,
    hasPermission: (permission: string) => {
      return productCode ? (query.data?.[productCode]?.includes(permission) ?? false) : false;
    },
  };
};

export const useHasHrPermission = (enabled = true) => useFetchPermissions("hr", enabled);

export const useUpdateUserPermission = (productCode: ProductCode) => {
  const queryClient = useQueryClient();

  const queryKey = usersQueryKeyMap[productCode];

  return useMutation<{ status?: string }, ApiError, HrUserUpdateModel>({
    mutationFn: async (employee: HrUserUpdateModel): Promise<{ status?: string }> => {
      const { data } = await bffAxios.patch(`/users/${productCode}/${employee.userId}`, employee);

      return data;
    },
    onSuccess: async (data, employee) => {
      if (productCode === "ats" && data.status === "PENDING") {
        queryClient.invalidateQueries({ queryKey, refetchType: "none" });
        queryClient.setQueryData<AtsAdminSettingsUser[]>(queryKey, (users) =>
          users?.map((user) => (user.userId === employee.userId ? user : user))
        );

        return;
      }

      await queryClient.invalidateQueries({ queryKey });

      queryClient.invalidateQueries({ queryKey: securityGroupsQueryKeyMap[productCode] });
    },
  });
};

export const useUpdateBulkUserPermission = (productCode: ProductCode) => {
  const queryClient = useQueryClient();
  const queryKey = usersQueryKeyMap[productCode];

  return useMutation<{ assignmentId?: string; status?: string }, ApiError, HrUserBulkUpdateModel>({
    mutationFn: async (
      employees: HrUserBulkUpdateModel
    ): Promise<{ assignmentId?: string; status?: string }> => {
      const { data } = await bffAxios.post<{ assignmentId?: string; status?: string }>(
        `/users/${productCode}/bulk-assign-security-groups`,
        employees
      );

      return data;
    },
    onSuccess: async (data, employees) => {
      if (productCode === "ats" && data.status === "PENDING") {
        queryClient.invalidateQueries({ queryKey, refetchType: "none" });
        queryClient.setQueryData<AtsAdminSettingsUser[]>(queryKey, (users) =>
          users?.map((user) => (employees.users.includes(user.userId) ? user : user))
        );

        return;
      }

      await queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useSendWelcomeEmail = () => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, string>({
    mutationFn: async (userId: string): Promise<void> => {
      await bffAxios.post(`/users/ats/${userId}/welcome-email`);
    },
    // Preserve the optimistic security groups while the asynchronous ATS assignment catches up.
    // Mark the query stale without immediately refetching, so a premature response cannot replace
    // the optimistic row with the user's not-yet-synced security groups.
    onSettled: (_data, error, userId) => {
      // A failed request does not prove that delivery failed; preserve the prior status.
      queryClient.setQueryData<AtsAdminSettingsUser[]>(platformQueryKeys.atsUsers(), (users) =>
        users?.map((user) =>
          user.userId !== userId
            ? user
            : !error
              ? { ...user, emailStatus: "SENT" }
              : user.emailStatus
                ? user
                : { ...user, emailStatus: "FAILED" }
        )
      );
      queryClient.invalidateQueries({
        queryKey: platformQueryKeys.atsUsers(),
        refetchType: "none",
      });
    },
  });
};

export const useCreatePlatformUser = () => {
  const queryClient = useQueryClient();
  const queryKey = usersQueryKeyMap.ats;

  return useMutation<PlatformUser, ApiError, CreatePlatformUserModel>({
    mutationFn: async (user: CreatePlatformUserModel): Promise<PlatformUser> => {
      const { data } = await bffAxios.post<PlatformUser>("/users/ats", {
        displayName: user.displayName?.trim(),
        email: user.email?.trim(),
        password: "",
        securityGroupIds: user.securityGroups,
      });

      return data;
    },
    onSuccess: (platformUser) => {
      const createdUser: AtsAdminSettingsUser = {
        userId: platformUser.id,
        name: platformUser.displayName,
        email: platformUser.email,
        atsUserReady: false,
        atsUserProvisioningPending: true,
        securityGroups: [],
      };

      queryClient.setQueryData<AtsAdminSettingsUser[]>(queryKey, (users) =>
        users ? [...users.filter((user) => user.userId !== createdUser.userId), createdUser] : users
      );
      queryClient.invalidateQueries({ queryKey: queryKey, refetchType: "active" });
    },
  });
};
